'use client';

import React, { useState, useEffect } from 'react';
import { Drawer, IconButton, Tooltip } from '@mui/material';
import { X, HelpCircle, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import CustomSwitch from './CustomSwitch';
import { useTranslation } from '@/lib/i18n';
import WebsiteIcon from '../Icons/WebsiteIcon';
import LiveDataIcon from '../Icons/LiveDataIcon';
import TwitterIcon from '../Icons/TwitterIcon';
import { useTheme } from 'next-themes';

interface SourceSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface SourceSettings {
  sources: {
    liveData: boolean;
    website: boolean;
    cryptoTwitter: boolean;
  };
  credibilityThreshold: number;
  recency: string;
  customRange: boolean;
  startDate: string;
  endDate: string;
}

const SourceSettingsDrawer: React.FC<SourceSettingsDrawerProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [settings, setSettings] = useState<SourceSettings>(() => {
    // 从localStorage读取设置
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sourceSettings');
      if (saved) {
        return JSON.parse(saved);
      }
    }
    return {
      sources: {
        liveData: true,
        website: true,
        cryptoTwitter: false,
      },
      credibilityThreshold: 1,
      recency: 'allTime',
      customRange: false,
      startDate: '',
      endDate: '',
    };
  });

  // 保存设置到localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sourceSettings', JSON.stringify(settings));
    }
  }, [settings]);

  const handleSourceToggle = (source: keyof typeof settings.sources) => {
    setSettings((prev) => ({
      ...prev,
      sources: {
        ...prev.sources,
        [source]: !prev.sources[source],
      },
    }));
  };

  const handleCredibilityChange = (rating: number) => {
    setSettings((prev) => ({
      ...prev,
      credibilityThreshold: rating,
    }));
  };

  const handleRecencyChange = (value: string) => {
    setSettings((prev) => ({
      ...prev,
      recency: value,
    }));
  };

  const handleCustomRangeToggle = () => {
    setSettings((prev) => ({
      ...prev,
      customRange: !prev.customRange,
    }));
  };

  const handleConfirm = () => {
    // 保存设置并关闭
    if (typeof window !== 'undefined') {
      localStorage.setItem('sourceSettings', JSON.stringify(settings));
    }
    onClose();
  };

  const handleCancel = () => {
    // 恢复之前的设置
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sourceSettings');
      if (saved) {
        setSettings(JSON.parse(saved));
      }
    }
    onClose();
  };

  const recencyOptions = [
    { value: 'allTime', label: t('All Time') },
    { value: 'last24h', label: t('Last 24h') },
    { value: 'last7d', label: t('Last 7d') },
    { value: 'last1m', label: t('Last 1m') },
    { value: 'last1y', label: t('Last 1y') },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 375,
          bgcolor: theme === 'dark' ? '#1b1b1b' : '#ffffff',
          color: theme === 'dark' ? 'white' : 'black',
        },
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-[50px] border-b border-gray-200 dark:border-[#272727]">
          <h2 className="text-lg font-semibold text-text-primary">{t('Source Settings')}</h2>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: theme === 'dark' ? 'white' : 'black' }}
          >
            <X size={16} className="text-text-primary" />
          </IconButton>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-2">
          {/* Source Type */}
          <div className="mb-10">
            <h3 className="text-base font-bold mb-8 text-text-primary">{t('Source Type')}</h3>

            <div className="space-y-6">
              {/* Live Data */}
              <div className="flex items-start justify-between">
                <div className="flex gap-2.5">
                  <LiveDataIcon />
                  <div>
                    <div className="text-sm font-semibold text-text-primary">{t('Live Data')}</div>
                    <div className="text-xs text-text-secondary dark:text-text-tertiary mt-2">
                      {t('Real-time Market and On-Chain metrics')}
                    </div>
                  </div>
                </div>
                <CustomSwitch
                  checked={settings.sources.liveData}
                  onChange={() => handleSourceToggle('liveData')}
                  size="small"
                />
              </div>

              {/* Website */}
              <div className="flex items-start justify-between">
                <div className="flex gap-2">
                  <WebsiteIcon />
                  <div>
                    <div className="text-sm font-semibold text-text-primary">{t('Website')}</div>
                    <div className="text-xs text-text-secondary dark:text-text-tertiary mt-2">
                      {t('News & Information across the Internet')}
                    </div>
                  </div>
                </div>
                <CustomSwitch
                  checked={settings.sources.website}
                  onChange={() => handleSourceToggle('website')}
                  size="small"
                />
              </div>

              {/* Crypto Twitter */}
              <div className="flex items-start justify-between">
                <div className="flex gap-2">
                  <TwitterIcon />
                  <div>
                    <div className="text-sm font-semibold text-text-primary">
                      {t('Crypto Twitter')}
                    </div>
                    <div className="text-xs text-text-secondary dark:text-text-tertiary mt-2">
                      {t('Social sentiment and KOL tweets')}
                    </div>
                  </div>
                </div>
                <CustomSwitch
                  checked={settings.sources.cryptoTwitter}
                  onChange={() => handleSourceToggle('cryptoTwitter')}
                  size="small"
                />
              </div>
            </div>
          </div>

          {/* Credibility Threshold */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-base font-bold text-text-primary">
                {t('Credibility Threshold')}
              </h3>
              <Tooltip
                title={
                  <div className="bg-[#1d1d21] p-5 rounded-2xl border border-[#2f2f2f] max-w-[375px]">
                    <div className="space-y-5">
                      <div>
                        <h4 className="text-white text-sm font-semibold mb-2">
                          {t('What is the credibility level?')}
                        </h4>
                        <p className="text-text-tertiary text-sm leading-5">
                          {t(
                            'AI answers come from different data sources, and their reliability may vary. We use a 5-star scale to show trustworthiness — 5 stars is highest, 1 star is lowest.',
                          )}
                        </p>
                      </div>

                      <div className="space-y-4">
                        {/* 5 Stars - Authoritative Source */}
                        <div className="pb-4 border-b border-[#2f2f2f]">
                          <div className="flex items-start gap-2 mb-2">
                            <div className="flex gap-0">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <Star
                                  key={i}
                                  size={14}
                                  className="fill-yellow-500 text-yellow-500"
                                />
                              ))}
                            </div>
                            <span className="text-white text-sm font-semibold">
                              {t('Authoritative Source')}
                            </span>
                          </div>
                          <p className="text-text-tertiary text-xs leading-4">
                            {t(
                              'From official announcements, industry authorities, or well-verified first-hand data.',
                            )}
                          </p>
                        </div>

                        {/* 4 Stars - Highly Reliable */}
                        <div className="pb-4 border-b border-[#2f2f2f]">
                          <div className="flex items-start gap-2 mb-2">
                            <div className="flex gap-0">
                              {[1, 2, 3, 4].map((i) => (
                                <Star
                                  key={i}
                                  size={14}
                                  className="fill-yellow-500 text-yellow-500"
                                />
                              ))}
                            </div>
                            <span className="text-white text-sm font-semibold">
                              {t('Highly Reliable')}
                            </span>
                          </div>
                          <p className="text-text-tertiary text-xs leading-4">
                            {t(
                              'From reputable media, leading data platforms, or well-recognized expert analysis.',
                            )}
                          </p>
                        </div>

                        {/* 3 Stars - Reliable Reference */}
                        <div className="pb-4 border-b border-[#2f2f2f]">
                          <div className="flex items-start gap-2 mb-2">
                            <div className="flex gap-0">
                              {[1, 2, 3].map((i) => (
                                <Star
                                  key={i}
                                  size={14}
                                  className="fill-yellow-500 text-yellow-500"
                                />
                              ))}
                            </div>
                            <span className="text-white text-sm font-semibold">
                              {t('Reliable Reference')}
                            </span>
                          </div>
                          <p className="text-text-tertiary text-xs leading-4">
                            {t(
                              'From generally trustworthy community discussions, professional forums, or stable data sources.',
                            )}
                          </p>
                        </div>

                        {/* 2 Stars - Questionable Source */}
                        <div className="pb-4 border-b border-[#2f2f2f]">
                          <div className="flex items-start gap-2 mb-2">
                            <div className="flex gap-0">
                              {[1, 2].map((i) => (
                                <Star
                                  key={i}
                                  size={14}
                                  className="fill-yellow-500 text-yellow-500"
                                />
                              ))}
                            </div>
                            <span className="text-white text-sm font-semibold">
                              {t('Questionable Source')}
                            </span>
                          </div>
                          <p className="text-text-tertiary text-xs leading-4">
                            {t(
                              'From partially unverified public data or secondary information channels.',
                            )}
                          </p>
                        </div>

                        {/* 1 Star - Low Credibility */}
                        <div>
                          <div className="flex items-start gap-2 mb-2">
                            <Star size={14} className="fill-yellow-500 text-yellow-500" />
                            <span className="text-white text-sm font-semibold">
                              {t('Low Credibility')}
                            </span>
                          </div>
                          <p className="text-text-tertiary text-xs leading-4">
                            {t(
                              'From anonymous, unclear, or highly subjective sources that may have significant inaccuracies.',
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                placement="left"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'transparent',
                      padding: 0,
                      maxWidth: 'none',
                      '& .MuiTooltip-arrow': {
                        color: '#2f2f2f',
                      },
                    },
                  },
                }}
              >
                <HelpCircle size={16} className="text-text-secondary cursor-help" />
              </Tooltip>
            </div>

            <div className="flex gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} onClick={() => handleCredibilityChange(star)} className="p-0">
                  <Star
                    size={24}
                    className={cn(
                      'transition-colors',
                      star <= settings.credibilityThreshold
                        ? 'fill-yellow-500 text-yellow-500'
                        : 'text-[#595959] fill-[#595959]',
                    )}
                  />
                </button>
              ))}
            </div>
            <div className="text-xs text-text-secondary dark:text-text-tertiary">
              {settings.credibilityThreshold === 1
                ? t('No threshold')
                : t('{count} star{plural} minimum', {
                    count: settings.credibilityThreshold,
                    plural: settings.credibilityThreshold > 1 ? 's' : '',
                  })}
            </div>
          </div>

          {/* Recency */}
          <div>
            <h3 className="text-base font-bold mb-6 text-text-primary">{t('Recency')}</h3>

            <div className="flex flex-wrap gap-3 mb-4">
              {recencyOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleRecencyChange(option.value)}
                  className={cn(
                    'px-2.5 py-2 rounded-xl text-xs w-[103px] transition-colors',
                    settings.recency === option.value
                      ? 'primary-btn-bg font-semibold'
                      : 'bg-bg-main text-text-secondary',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Custom Range */}
            <div className="flex flex-col gap-4 mt-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-text-secondary">{t('Custom Range')}</span>
                <CustomSwitch
                  checked={settings.customRange}
                  onChange={handleCustomRangeToggle}
                  size="small"
                />
              </div>

              {/* Date Range Picker - 显示当custom range开启时 */}
              {settings.customRange && (
                <div className="bg-bg-main rounded-xl px-2.5 py-2 mb-6 flex items-center gap-3">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-text-primary"
                  >
                    <rect
                      x="2"
                      y="3.5"
                      width="12"
                      height="10.5"
                      rx="1"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5 2V5"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11 2V5"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 6.5H14"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <rect x="4.5" y="8.5" width="1.5" height="1.5" fill="currentColor" />
                    <rect x="7.25" y="8.5" width="1.5" height="1.5" fill="currentColor" />
                    <rect x="10" y="8.5" width="1.5" height="1.5" fill="currentColor" />
                    <rect x="4.5" y="11" width="1.5" height="1.5" fill="currentColor" />
                    <rect x="7.25" y="11" width="1.5" height="1.5" fill="currentColor" />
                  </svg>
                  <input
                    type="text"
                    placeholder={t('Start date')}
                    value={settings.startDate}
                    onChange={(e) =>
                      setSettings((prev) => ({ ...prev, startDate: e.target.value }))
                    }
                    className="bg-transparent text-text-secondary text-xs placeholder-gray-400 dark:placeholder-[#757575] outline-hidden w-[110px]"
                    onFocus={(e) => {
                      e.target.type = 'date';
                      e.target.style.color = '#ffffff';
                    }}
                    onBlur={(e) => {
                      if (!e.target.value) {
                        e.target.type = 'text';
                        e.target.style.color = '#757575';
                      }
                    }}
                  />
                  <span className="text-text-secondary text-xs">—</span>
                  <input
                    type="text"
                    placeholder={t('End date')}
                    value={settings.endDate}
                    onChange={(e) => setSettings((prev) => ({ ...prev, endDate: e.target.value }))}
                    className="bg-transparent text-text-secondary text-xs placeholder-gray-400 dark:placeholder-[#757575] outline-hidden w-[110px]"
                    onFocus={(e) => {
                      e.target.type = 'date';
                      e.target.style.color = '#ffffff';
                    }}
                    onBlur={(e) => {
                      if (!e.target.value) {
                        e.target.type = 'text';
                        e.target.style.color = '#757575';
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-4 px-5 py-4 border-t border-gray-200 dark:border-[#272727]">
          <button
            onClick={handleCancel}
            className="flex-1 py-3 rounded-2xl bg-bg-main text-text-primary dark:text-text-secondary text-base hover:opacity-80 transition-opacity"
          >
            {t('Cancel')}
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 py-3 rounded-2xl primary-btn-bg text-base hover:opacity-80 transition-opacity"
          >
            {t('Confirm')}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default SourceSettingsDrawer;
